/**
 * Guarda a rota que o usuário tentou abrir antes de ser mandado ao Keycloak,
 * para o callback devolver ele exatamente ali em vez de despejar na /dashboard.
 *
 * Fica em sessionStorage de propósito: quem escreve e quem lê é a mesma aba.
 * Abrir um link do menu em nova aba cria uma aba sem token (os tokens também
 * são por aba), e é justamente esse caminho que precisa lembrar o destino.
 */
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

/** Rotas que nunca devem ser o destino pós-login — voltaria a rodar o fluxo. */
const NON_DESTINATIONS = [
  "/login",
  "/login/musa",
  "/login-redirect",
  "/login-redirect/musa",
];

/**
 * Só aceita caminho relativo da própria aplicação. Barra `//evil.com`,
 * `https://evil.com` e `javascript:` — sem isso, o valor guardado viraria um
 * open redirect controlado por quem montar a URL.
 */
export const isSafeInternalPath = (path: string | null | undefined): boolean => {
  if (!path) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.startsWith("/\\")) return false;
  return !NON_DESTINATIONS.includes(path.split("?")[0]);
};

export const savePostLoginRedirect = (path: string): void => {
  if (!isSafeInternalPath(path)) return;
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
};

/** Lê e descarta o destino guardado. Devolve `null` se não houver um válido. */
export const consumePostLoginRedirect = (): string | null => {
  const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return isSafeInternalPath(stored) ? stored : null;
};

const EXPLICIT_LOGOUT_KEY = "post_login_explicit_logout";

/**
 * Marca que a saída da rota atual é um logout explícito (clique em "Sair"),
 * não uma sessão que expirou no meio do uso.
 *
 * O `AuthProvider` detecta "não autenticado" por polling, não na hora do
 * clique — sem essa marca, ele acha que a página atual é a que o usuário
 * "estava tentando abrir" e guarda ela como destino pós-login (mesmo
 * mecanismo do `savePostLoginRedirect`, pensado pra aba nova/sessão
 * expirada). No logout explícito o usuário está de saída de propósito, então
 * o destino certo depois de logar de novo é a dashboard, não a página antiga.
 *
 * Chamar depois de `sessionStorage.clear()` no logout, pra sobreviver a ele.
 */
export const markExplicitLogout = (): void => {
  sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, "1");
};

/** Lê e descarta a marca de logout explícito. */
export const consumeIsExplicitLogout = (): boolean => {
  const wasExplicitLogout = sessionStorage.getItem(EXPLICIT_LOGOUT_KEY) === "1";
  sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  return wasExplicitLogout;
};
